import { Link, Navigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { KeepExploring } from "../components/RelatedLinks";
import { useLang, useLp } from "../lib/i18n";

const seoTitle = "IA para reunião em inglês: guia 2026 para devs e sales brasileiros";
const seoDescription =
  "Guia prático de IA para reunião em inglês: como participar melhor, responder perguntas difíceis e usar o Minuta durante a call, sem bot entrando na call.";

const phraseExamples = [
  {
    moment: "Quando você precisa ganhar tempo",
    english: "Let me think about the best way to answer that.",
    use: "Use quando a pergunta chega rápido demais e você precisa de cinco segundos para organizar a resposta sem parecer travado.",
  },
  {
    moment: "Quando não entendeu a pergunta",
    english: "Could you rephrase that last part? I want to make sure I answer the right question.",
    use: "Melhor do que fingir que entendeu. Mostra cuidado, reduz risco e compra clareza.",
  },
  {
    moment: "Quando a resposta técnica é longa",
    english: "There are two parts to this. First, the current behavior. Second, the trade-off we recommend.",
    use: "Ajuda a separar raciocínio técnico, contexto e recomendação sem virar monólogo.",
  },
  {
    moment: "Quando precisa discordar",
    english: "I see the point, but I would separate the product risk from the implementation risk.",
    use: "Discorda sem soar defensivo e já propõe um enquadramento mais útil para a conversa.",
  },
];

const aiHelp = [
  {
    title: "Detectar quando a pergunta é para você",
    body: "Em call com várias pessoas, o problema não é só inglês. É perceber que a pergunta mudou de direção e agora esperam sua opinião. O Minuta acompanha a fala em tempo real e destaca o momento em que uma pergunta pede resposta sua.",
  },
  {
    title: "Sugerir uma resposta em inglês em poucos segundos",
    body: "Você não precisa decorar frases prontas. A IA usa o contexto da conversa para montar uma resposta curta, natural e específica. A ideia não é falar por você, mas tirar a pressão do primeiro rascunho.",
  },
  {
    title: "Guardar o histórico para follow-up",
    body: "Depois da reunião, você ainda precisa mandar recap, próximos passos e decisões. A transcrição e o resumo ajudam a transformar uma call difícil em um follow-up claro, sem depender da memória.",
  },
];

const comparisons = [
  {
    label: "Bot de reunião",
    verdict: "Entra como participante, aparece para todo mundo e costuma focar na ata depois da call.",
  },
  {
    label: "Notetaker clássico",
    verdict: "Ajuda no pós-reunião, mas chega tarde demais quando você precisa responder ao vivo.",
  },
  {
    label: "Aula de inglês",
    verdict: "Melhora sua base no longo prazo, mas não resolve aquela pergunta técnica daqui a três segundos.",
  },
  {
    label: "Minuta",
    verdict: "Roda no canto da tela, sem bot na call, para sugerir respostas enquanto a reunião acontece.",
  },
];

const checklist = [
  "Antes da call, abra o Minuta e confira se o áudio do sistema está sendo capturado.",
  "Entre no Zoom, Google Meet ou Microsoft Teams normalmente; nenhum plugin precisa ser instalado.",
  "Quando uma pergunta difícil aparecer, leia a sugestão curta antes de responder.",
  "Use a resposta como ponto de partida, não como script rígido.",
  "Depois da reunião, revise resumo, decisões e próximos passos antes de mandar follow-up.",
];

const faqs = [
  {
    q: "Vai entrar um bot na reunião?",
    a: "Não. O Minuta roda no seu computador e não entra como participante no Zoom, Meet ou Teams. As outras pessoas não veem um bot na lista da chamada.",
  },
  {
    q: "Funciona em Google Meet, Zoom e Microsoft Teams?",
    a: "Sim. Como o Minuta captura o áudio do computador, ele funciona com as principais plataformas de reunião e também com calls em navegador.",
  },
  {
    q: "Meu inglês não é fluente. Ainda faz sentido?",
    a: "Sim. O produto foi pensado para brasileiros que já conseguem participar de reunião, mas travam quando precisam responder rápido, discordar, explicar trade-offs ou fazer follow-up em inglês.",
  },
  {
    q: "Onde entram LGPD e dados da reunião?",
    a: "O app captura o áudio no desktop e usa processamento cloud para transcrição, resumo e copiloto. O objetivo é manter o fluxo leve, sem bot e com retenção operacional mínima. A política de privacidade detalha as bases e fornecedores.",
  },
  {
    q: "Posso usar em call sigilosa?",
    a: "Depende da regra da sua empresa e do contrato com o cliente. Para assuntos sigilosos, confirme permissão antes de gravar ou transcrever. O Minuta não substitui aprovação legal ou política interna.",
  },
  {
    q: "R$22.50 é para sempre?",
    a: "Não. As primeiras 100 vagas Founding 100 travam R$22.50/mês por 12 meses. Depois das 100 vagas, o plano Pro volta para R$35/mês.",
  },
];

export function IaReuniaoIngles() {
  const lang = useLang();
  const lp = useLp();

  if (lang !== "pt") {
    return <Navigate to="/pt/ia-reuniao-ingles" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path="/ia-reuniao-ingles"
        alternateLangs={["pt"]}
        schema={["article", "breadcrumbList"]}
        schemaOptions={{
          article: {
            headline: seoTitle,
            datePublished: "2026-05-30",
            dateModified: "2026-05-30",
            author: "Minuta",
          },
          breadcrumbs: [
            { name: "Minuta", url: "/pt/" },
            { name: "IA para reunião em inglês", url: "/pt/ia-reuniao-ingles" },
          ],
        }}
      />
      <Header />

      <main className="flex-1">
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              Guia 2026 para brasileiros em calls globais
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              A IA que te ajuda na reunião em inglês — sem bot entrando na call.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              Minuta escuta a call, entende quando a pergunta é pra você e sugere a
              resposta em inglês em 3 segundos. Roda no canto da tela. Ninguém na
              chamada vê.
            </p>
            <p className="mt-5 font-mono text-[12px] uppercase tracking-[0.14em] text-ink/55 dark:text-paper/55">
              macOS · Windows · grátis por 7 dias · R$22.50/mês nas primeiras 100
              vagas
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to={lp + "/downloads?utm_source=seo&utm_medium=pillar&utm_campaign=ia_reuniao_ingles"}
                className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
              >
                Baixar o Minuta
              </Link>
              <Link
                to={lp + "/pricing"}
                className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
              >
                Ver Founding 100
              </Link>
            </div>
          </div>
        </section>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                O problema não é só saber inglês. É responder sob pressão.
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Todo dev, sales engineer, account executive ou founder brasileiro que
                trabalha com cliente global conhece o momento: a reunião está indo bem,
                você está acompanhando quase tudo, até que alguém faz uma pergunta
                aberta em inglês e a call inteira fica em silêncio esperando você
                responder. A ideia está na cabeça, mas não sai com a mesma velocidade.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Esse é o ponto onde uma IA para reunião em inglês precisa ser diferente
                de uma ferramenta de ata. A ata ajuda depois. O que decide confiança,
                negociação e autoridade técnica acontece durante a conversa: pedir
                esclarecimento, discordar com tato, explicar trade-off, dizer que algo
                não é possível e propor o próximo passo.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                A pergunta "como participar de reunião em inglês" costuma receber
                respostas genéricas: estude vocabulário, fale devagar, pratique
                pronúncia. Tudo isso ajuda. Mas em uma call real, você também precisa
                de suporte de contexto. Precisa saber o que acabou de ser perguntado,
                que parte da conversa importa e qual resposta curta destrava o fluxo.
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <div className="max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  Frases que salvam a call quando seu inglês trava
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                  Frase pronta não substitui raciocínio, mas ajuda a sair do branco.
                  O segredo é usar estruturas que organizam intenção: ganhar tempo,
                  pedir clareza, separar partes da resposta e discordar sem atrito.
                </p>
              </div>
              <div className="mt-10 grid gap-5 md:grid-cols-2">
                {phraseExamples.map((item) => (
                  <article
                    key={item.moment}
                    className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-6 dark:border-paper/12 dark:bg-paper/[0.02]"
                  >
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
                      {item.moment}
                    </p>
                    <p className="mt-4 font-display text-xl font-semibold leading-snug text-ink dark:text-paper">
                      {item.english}
                    </p>
                    <p className="mt-4 text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.use}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <div className="max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  Onde a IA ajuda durante a reunião
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                  Uma boa IA para reunião em inglês não deve tentar virar professor,
                  tradutor e notetaker ao mesmo tempo. Ela precisa focar no gargalo da
                  call: transformar contexto falado em uma resposta que você consegue
                  dizer com segurança.
                </p>
              </div>
              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {aiHelp.map((item) => (
                  <article
                    key={item.title}
                    className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-6 dark:border-paper/12 dark:bg-paper/[0.02]"
                  >
                    <h3 className="font-display text-xl font-semibold text-ink dark:text-paper">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Sem bot na call importa mais do que parece
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                Muitas empresas já usam notetakers, mas eles mudam a dinâmica da sala.
                Quando um bot entra como participante, todo mundo sabe que há uma
                gravação visível. Em reunião comercial, entrevista, negociação ou call
                técnica sensível, isso pode criar atrito antes da conversa começar.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                O Minuta segue outra abordagem: o app fica no seu desktop, captura o
                áudio da reunião e usa processamento cloud para entregar transcrição,
                resumo e copiloto. Você continua responsável por consentimento e pelas
                regras da sua empresa, mas não precisa adicionar um participante novo
                para receber ajuda enquanto fala.
              </p>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <div className="max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                  Bot, notetaker, aula de inglês ou copiloto?
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                  A categoria ainda é confusa. "Copiloto de reuniões" não deveria ser
                  só outro nome para ata automática. Para brasileiros em reunião em
                  inglês, o valor aparece quando a ferramenta ajuda antes da resposta
                  sair da sua boca.
                </p>
              </div>
              <div className="mt-10 divide-y divide-ink/10 rounded-2xl border border-ink/10 dark:divide-paper/12 dark:border-paper/12">
                {comparisons.map((item) => (
                  <div key={item.label} className="grid gap-3 p-5 md:grid-cols-[180px_1fr]">
                    <h3 className="font-display text-lg font-semibold text-ink dark:text-paper">
                      {item.label}
                    </h3>
                    <p className="text-[14px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.verdict}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Checklist para usar IA em reunião em inglês
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                O melhor setup é simples. Não transforme a ferramenta em mais uma
                coisa para gerenciar durante a call. Deixe o Minuta rodando, use as
                sugestões quando a conversa apertar e mantenha sua resposta humana.
              </p>
              <ol className="mt-8 space-y-4">
                {checklist.map((item, index) => (
                  <li
                    key={item}
                    className="grid grid-cols-[32px_1fr] gap-4 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 font-mono text-[12px] text-ink/60 dark:border-paper/15 dark:text-paper/60">
                      {index + 1}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Perguntas frequentes
              </h2>
              <div className="mt-10 divide-y divide-ink/10 dark:divide-paper/12">
                {faqs.map((item) => (
                  <article key={item.q} className="py-6 first:pt-0 last:pb-0">
                    <h3 className="font-display text-xl font-semibold text-ink dark:text-paper">
                      {item.q}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                      {item.a}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-24 text-center">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
                Founding 100
              </p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Entre nas primeiras 100 vagas por R$22.50/mês.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-ink/70 dark:text-paper/70">
                Se reunião em inglês já faz parte do seu trabalho, o Minuta foi feito
                para o momento em que você precisa responder com clareza agora, não
                depois da ata. As primeiras 100 vagas travam o preço por 12 meses.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={lp + "/downloads?utm_source=seo&utm_medium=pillar&utm_campaign=ia_reuniao_ingles"}
                  className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                >
                  Baixar o Minuta
                </Link>
                <Link
                  to={lp + "/pricing"}
                  className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
                >
                  Ver planos
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        <KeepExploring ids={["downloads", "pricing", "blog"]} />
      </main>

      <Footer />
    </div>
  );
}
